import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useNavigate } from 'react-router-dom';

const highlights = [
  {
    icon: 'ri-landscape-line',
    title: '四分之三的香港，是綠色',
    desc: '香港超過 40% 土地係受保護嘅郊野公園同綠化地帶。由大埔滘自然護理區嘅茂密森林，到市區綠洲如添馬公園、佐敦谷公園，大自然從來都唔係遙不可及——只係短短車程，你已經可以置身另一個世界。',
  },
  {
    icon: 'ri-walk-line',
    title: '森林浴 ≠ 行山',
    desc: '行山著重配速、完成路線同體能目標；森林浴卻完全相反——佢係「無目標」嘅。唔需要趕路、唔需要攻頂、甚至唔需要行好遠。你只需要喺樹林入面慢落嚟，打開五感，純粹存在。森林浴嘅重點唔係「做到啲咩」，而係「感受到啲咩」。',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: '15-20 分鐘的科學實證',
    desc: '研究證實，身處綠色空間短短 15-20 分鐘，就能夠顯著降低體內嘅皮質醇（壓力荷爾蒙）水平。當壓力指數回落，身體會由長期嘅「戰鬥或逃跑」（fight-or-flight）模式，切換到「休息與修復」（rest-and-repair）模式——心率減慢、肌肉放鬆、免疫系統恢復正常運作。',
  },
];

export default function ForestBathing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />
      <main>
        <section className="relative py-20 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm font-medium text-foreground-600 transition-colors cursor-pointer whitespace-nowrap mb-10"
            >
              <i className="ri-arrow-left-line"></i>
              返回主頁
            </button>

            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100/80 text-primary-800 text-xs font-semibold tracking-widest uppercase mb-6 whitespace-nowrap">
                <i className="ri-tree-line"></i>
                Shinrin-yoku · 森林浴
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-medium text-foreground-950 leading-tight">
                在香港森林浴
              </h1>
              <p className="mt-6 text-sm md:text-base text-foreground-600 leading-relaxed max-w-lg mx-auto">
                森林浴（Shinrin-yoku）源於日本，係一種以科學為基礎、無需目標嘅大自然沉浸練習——專為生活節奏急促嘅都市人而設。
              </p>
              <div className="mt-8 max-w-3xl mx-auto rounded-2xl overflow-hidden">
                <img
                  src="https://storage.helloreaddy.io/project_files/80621f35-4526-4d61-b800-6d2f08be7197/528ba011-193e-42c6-ad96-af8b5a58a0ea_compressed_20250427_11460713.webp"
                  alt="鬱鬱蔥蔥的綠色森林小徑，充滿療癒氛圍"
                  className="w-full h-56 md:h-72 object-cover object-center"
                />
              </div>
            </div>

            {/* Three highlight cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
              {highlights.map((h, idx) => (
                <div
                  key={idx}
                  className={`rounded-2xl p-6 md:p-7 flex flex-col ${
                    idx === 1
                      ? 'bg-primary-500 text-background-50 border border-primary-500'
                      : 'bg-background-100 border border-background-200/70'
                  }`}
                >
                  <div
                    className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 ${
                      idx === 1 ? 'bg-background-50/20 text-background-50' : 'bg-primary-50 text-primary-600'
                    }`}
                  >
                    <i className={`${h.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-3">{h.title}</h3>
                  <p
                    className={`text-sm leading-relaxed flex-1 ${
                      idx === 1 ? 'text-background-50/90' : 'text-foreground-600'
                    }`}
                  >
                    {h.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Ocean coastline image */}
            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src="https://storage.helloreaddy.io/project_files/80621f35-4526-4d61-b800-6d2f08be7197/624ea31a-15e3-4b70-a601-b4e5db7d14a3_compressed_Screenshot_20260814_172141_Instagram1.webp"
                alt="壯麗的岩石海岸線與蔚藍大海，自然的力量"
                className="w-full h-56 md:h-72 object-cover object-center"
              />
            </div>

            {/* Middle: Forest Bathing vs Hiking comparison */}
            <div className="rounded-3xl bg-background-100 border border-background-200/70 p-8 md:p-10 mb-12">
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 text-center mb-8">
                森林浴 vs 行山：有咩分別？
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-2xl bg-background-50 border border-background-200/60 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-speed-up-line"></i>
                    </div>
                    <h4 className="font-heading text-base font-semibold text-foreground-900">行山</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-foreground-600 leading-relaxed">
                    <li className="flex gap-2">
                      <span className="text-secondary-500 mt-1 shrink-0">•</span>
                      有明確路線同目的地
                    </li>
                    <li className="flex gap-2">
                      <span className="text-secondary-500 mt-1 shrink-0">•</span>
                      著重體能消耗同心率提升
                    </li>
                    <li className="flex gap-2">
                      <span className="text-secondary-500 mt-1 shrink-0">•</span>
                      以完成路線為目標
                    </li>
                    <li className="flex gap-2">
                      <span className="text-secondary-500 mt-1 shrink-0">•</span>
                      過程中可能較專注於腳下步伐
                    </li>
                  </ul>
                </div>
                <div className="rounded-2xl bg-primary-50 border border-primary-100/60 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-leaf-line"></i>
                    </div>
                    <h4 className="font-heading text-base font-semibold text-foreground-900">森林浴</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-foreground-600 leading-relaxed">
                    <li className="flex gap-2">
                      <span className="text-primary-500 mt-1 shrink-0">•</span>
                      無特定路線，隨心而行
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary-500 mt-1 shrink-0">•</span>
                      著重感官體驗：望、聽、聞、觸
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary-500 mt-1 shrink-0">•</span>
                      無任何目標，純粹「存在」
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary-500 mt-1 shrink-0">•</span>
                      每一步都係同大自然嘅對話
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Science section */}
            <div className="rounded-3xl bg-background-100 border border-background-200/70 p-8 md:p-10 mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className="ri-test-tube-line text-lg"></i>
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                  科學點樣解釋森林浴嘅效果？
                </h3>
              </div>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                當你身處綠色空間約 15-20 分鐘，體內過高嘅皮質醇就會開始回落。皮質醇係我哋面對壓力時分泌嘅荷爾蒙——短期嚟講佢幫我哋應付挑戰，但長期高企就會導致慢性疲勞、免疫力下降、甚至情緒困擾。森林浴嘅關鍵，就係俾身體一個清晰嘅訊號：「而家安全喇，可以放鬆。」呢個時候，身體由交感神經主導嘅「戰鬥或逃跑」狀態，切換到副交感神經主導嘅「休息與修復」狀態——心率減慢、血壓回落、肌肉放鬆、免疫細胞恢復活躍。
              </p>
            </div>

            {/* Closing message */}
            <div className="text-center max-w-xl mx-auto">
              <div className="rounded-2xl bg-accent-50 border border-accent-100/60 p-8">
                <i className="ri-double-quotes-l text-3xl text-accent-300 mb-4 block"></i>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  給每一位忙碌的你——尤其係喺事業同家庭之間努力平衡嘅女性。你值得將時間還俾自己。搵一個週末、甚至係放工後嘅半個鐘，行去最近嘅樹下，唔需要做啲咩，只需要喺度。讓世界喺你身邊慢慢軟化、安靜落嚟。你嘅身心，會感謝你。
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}